/**
 * QuoteBuilder — rebuilt to mirror ServiceNotesModal's editor (iter138.9)
 * ─────────────────────────────────────────────────────────────────────────
 * Single-source quote line-item editor used in:
 *   • The "Quote (phone)" service-type of QuickBookingModal
 *   • The /quotes/{id} detail page (edit + follow-up)
 *
 * Visual + behavioural parity with Service Notes Quote Builder:
 *   • Category pills: Part & Labour · Part only · Labour only · Tyre
 *   • Parts fields HIDDEN on "Labour only"
 *   • Labour fields HIDDEN on "Part only" and "Tyre"
 *   • Tyre row → margin LOCKED 30 % + flat-rate per tyre (fit + balance + valve)
 *   • ORP → RRP AUTO-FILL at ×1.30 (editable after)
 *   • Default margin = 30 %
 *   • Pricing math via shared computeRowPricing() — same as InvoiceEdit / ServiceNotes
 *
 * Public API (unchanged):
 *   <QuoteBuilder rows onChange showOrp />
 *   + named export `buildQuoteSmsBody({ rows, customerName, vehicleRego, ... })`
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Pencil, Check, X, Trash2, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import SupplierCombobox from '@/components/invoices/SupplierCombobox';
import { computeRowPricing, orpToRrp as orpToRrpHelper, DEFAULT_MARGIN_PCT as LIB_DEFAULT_MARGIN, DEFAULT_ORP_MARGIN_PCT } from '@/lib/pricing';
import { UNIT_OPTIONS } from '@/lib/units';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_LABOUR_RATE = 125;
const DEFAULT_MARGIN_PCT = LIB_DEFAULT_MARGIN;

// iter225 — per-site pricing defaults (set on the Invoices page). Seed NEW
// drafts only — existing/quoted rows are never touched. Cached per page
// load so every builder mount doesn't refetch.
let _pricingDefaultsCache = null;
export async function getPricingDefaults() {
  if (_pricingDefaultsCache) return _pricingDefaultsCache;
  try {
    const res = await fetch(`${BACKEND_URL}/api/pricing-defaults`, { credentials: 'include' });
    if (res.ok) {
      const d = await res.json();
      _pricingDefaultsCache = {
        labour_rate: Number(d.labour_rate) || DEFAULT_LABOUR_RATE,
        parts_margin_pct: Number(d.parts_margin_pct) || DEFAULT_MARGIN_PCT,
        orp_margin_pct: d.orp_margin_pct != null ? Number(d.orp_margin_pct) : DEFAULT_ORP_MARGIN_PCT,
      };
      return _pricingDefaultsCache;
    }
  } catch (_e) { /* fall through to static defaults */ }
  return { labour_rate: DEFAULT_LABOUR_RATE, parts_margin_pct: DEFAULT_MARGIN_PCT, orp_margin_pct: DEFAULT_ORP_MARGIN_PCT };
}
export function clearPricingDefaultsCache() { _pricingDefaultsCache = null; }
// Sync read of the workshop ORP→RRP margin (cache is warm by the time any
// editor is open). Used by EditorBlock's ORP auto-fill.
export function getCachedOrpMargin() {
  return _pricingDefaultsCache?.orp_margin_pct ?? DEFAULT_ORP_MARGIN_PCT;
}

const CATEGORY_OPTIONS = [
  { value: 'part_and_labour', label: 'Part & Labour' },
  { value: 'part',            label: 'Part only' },
  { value: 'labour',          label: 'Labour only' },
  { value: 'tyre',            label: 'Tyre (ORP + margin + flat)' },
];

const fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const EMPTY_DRAFT = {
  category: 'part_and_labour',
  description: '',
  quantity: 1,
  parts_supplier: '',
  parts_cost_price: '',
  parts_price: '',
  parts_margin_pct: DEFAULT_MARGIN_PCT,
  labour_hours: '',
  labour_rate: DEFAULT_LABOUR_RATE,
  tyre_flat_rate: '',
  unit: 'each',
};

// Draft seeded with the per-site pricing defaults (new rows only).
function blankDraft(defaults) {
  return {
    ...EMPTY_DRAFT,
    parts_margin_pct: defaults?.parts_margin_pct ?? DEFAULT_MARGIN_PCT,
    labour_rate: defaults?.labour_rate ?? DEFAULT_LABOUR_RATE,
  };
}

function buildRowFromDraft(draft, totals) {
  const cat = draft.category || 'part_and_labour';
  const isTyre = cat === 'tyre';
  const wantsParts = cat === 'part' || cat === 'part_and_labour' || isTyre;
  const wantsLabour = cat === 'labour' || cat === 'part_and_labour';
  // iter225 — tyre margin is MANUAL (blank = 0%), same as other categories.
  const margin = Number(draft.parts_margin_pct) || 0;
  // Tyre rows ALSO capture allocated hours for mechanic productivity
  // tracking — they DON'T affect the price (which is ORP×1.30 + flat),
  // they only flow as `quoted_labour_hours` into the Job Card task so
  // the fitting mechanic gets credited for the bench time. labour_rate
  // stays 0 on tyre rows so the pricing math is untouched.
  const tyreAllocHours = Number(draft.labour_hours) || 0;
  return {
    line_id: `qli_${Math.random().toString(36).slice(2, 10)}`,
    description: (draft.description || '').trim(),
    category: cat,
    quantity: Number(draft.quantity) || 1,
    parts_supplier: wantsParts ? ((draft.parts_supplier || '').trim() || null) : null,
    parts_cost_price: wantsParts ? (Number(draft.parts_cost_price) || 0) : 0,
    // No RRP concept on tyre rows — parts_price stays 0.
    parts_price: isTyre ? 0 : (wantsParts ? (Number(draft.parts_price) || 0) : 0),
    parts_margin_pct: wantsParts ? margin : 0,
    unit: draft.unit || (cat === 'labour' ? 'hours' : 'each'),
    labour_hours: isTyre
      ? tyreAllocHours
      : (wantsLabour ? (Number(draft.labour_hours) || 0) : 0),
    labour_rate: isTyre ? 0 : (wantsLabour ? (Number(draft.labour_rate) || 0) : 0),
    tyre_flat_rate: isTyre ? (Number(draft.tyre_flat_rate) || 0) : null,
    line_total: Number(totals.line_total.toFixed(2)),
    order: 0,
  };
}

function rowToDraft(row, defaults) {
  let cat = row.category;
  if (!cat) {
    const hasTyreFlat = (Number(row.tyre_flat_rate) || 0) > 0;
    const hasParts = (Number(row.parts_price) || 0) > 0;
    const hasLabour = (Number(row.labour_hours) || 0) > 0;
    cat = hasTyreFlat ? 'tyre'
      : hasParts && !hasLabour ? 'part'
      : !hasParts && hasLabour ? 'labour'
      : 'part_and_labour';
  }
  return {
    category: cat,
    description: row.description || '',
    quantity: row.quantity ?? 1,
    parts_supplier: row.parts_supplier || '',
    parts_cost_price: row.parts_cost_price ?? '',
    parts_price: row.parts_price ?? '',
    // Existing rows keep their stored margin. Legacy tyre rows saved under
    // the locked-30% era carry none → show 30 so the total doesn't shift.
    parts_margin_pct: row.parts_margin_pct ?? (cat === 'tyre' ? 30 : (defaults?.parts_margin_pct ?? DEFAULT_MARGIN_PCT)),
    labour_hours: row.labour_hours ?? '',
    labour_rate: row.labour_rate ?? (defaults?.labour_rate ?? DEFAULT_LABOUR_RATE),
    tyre_flat_rate: row.tyre_flat_rate ?? '',
    unit: row.unit || (cat === 'labour' ? 'hours' : 'each'),
  };
}

/**
 * EditorBlock — shared by "Add Row" and inline "Edit Row" forms.
 * Mirrors ServiceNotesModal layout to keep the SM mental model identical.
 */
function EditorBlock({ draft, setDraft, totals, onCommit, onCancel, showOrp = true, isEdit = false, showUnit = false, testIdPrefix = 'qb-editor' }) {
  const cat = draft.category || 'part_and_labour';
  const isTyre = cat === 'tyre';
  const hideParts = cat === 'labour';
  const hideLabour = cat === 'part';

  // ORP change → RRP auto-fill using the workshop ORP margin % from
  // Pricing Defaults (NOT on tyre — no RRP concept there).
  const onChangeOrp = (value) => {
    const patch = { parts_cost_price: value };
    const n = Number(value);
    if (!isTyre && isFinite(n) && n > 0) {
      patch.parts_price = orpToRrpHelper(value, getCachedOrpMargin());
    }
    setDraft(d => ({ ...d, ...patch }));
  };

  return (
    <div className="px-4 py-3 bg-emerald-50/40 border border-emerald-100 rounded-lg" data-testid={testIdPrefix}>
      {/* Category pills */}
      <div className="mb-2 flex items-center gap-2 flex-wrap">
        <label className="text-[10px] uppercase tracking-wide text-slate-500">Type</label>
        <div className="inline-flex bg-white rounded-md border border-slate-200 p-0.5" data-testid={`${testIdPrefix}-category`}>
          {CATEGORY_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setDraft(d => (
                opt.value === 'tyre'
                  // Tyre margin is MANUAL — clear any carried-over default
                  // (and RRP: no RRP concept on tyre rows).
                  ? { ...d, category: 'tyre', parts_margin_pct: '', parts_price: '' }
                  : { ...d, category: opt.value }
              ))}
              className={`px-2.5 py-1 text-[11px] rounded font-medium transition-all ${
                cat === opt.value
                  ? 'bg-emerald-600 text-white shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              data-testid={`${testIdPrefix}-category-${opt.value}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <span className="text-[10px] text-slate-400 ml-1">
          {cat === 'labour' && '• Parts fields hidden — labour only'}
          {cat === 'part' && '• Labour hidden — parts only'}
          {cat === 'tyre' && '• Tyre: ORP + your margin % + flat per tyre — no RRP'}
        </span>
      </div>

      {/* Row 1 — description, qty + parts block */}
      <div className="grid grid-cols-12 gap-2 items-end">
        <div className="col-span-12 md:col-span-4">
          <label className="text-[10px] uppercase tracking-wide text-slate-500">Description</label>
          <Input
            value={draft.description}
            onChange={(e) => setDraft(d => ({ ...d, description: e.target.value.toUpperCase() }))}
            placeholder="E.G. REPLACE FRONT BRAKE PADS & ROTORS"
            className="h-8 text-sm uppercase"
            data-testid={`${testIdPrefix}-description`}
            autoFocus={!isEdit}
          />
        </div>
        <div className="col-span-3 md:col-span-1">
          <label className="text-[10px] uppercase tracking-wide text-slate-500">Qty</label>
          <Input
            type="number" min="0" step="0.5"
            value={draft.quantity}
            onChange={(e) => setDraft(d => ({ ...d, quantity: e.target.value }))}
            className="h-8 text-sm text-right"
            data-testid={`${testIdPrefix}-qty`}
          />
        </div>

        {showUnit && (
          <div className="col-span-3 md:col-span-1">
            <label className="text-[10px] uppercase tracking-wide text-slate-500">Unit</label>
            <Select value={draft.unit || 'each'} onValueChange={(v) => setDraft(d => ({ ...d, unit: v }))}>
              <SelectTrigger className="h-8 text-sm" data-testid={`${testIdPrefix}-unit`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {UNIT_OPTIONS.map(u => (
                  <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Parts block — hidden on labour-only */}
        {!hideParts && (
          <>
            {showOrp && (
              <div className="col-span-4 md:col-span-2">
                <label
                  className="text-[10px] uppercase tracking-wide text-rose-600"
                  title="Our Real Price — supplier cost. RRP auto-fills at ORP × 1.30."
                >
                  ORP $ (cost)
                </label>
                <Input
                  type="number" min="0" step="0.01"
                  value={draft.parts_cost_price}
                  onChange={(e) => onChangeOrp(e.target.value)}
                  placeholder="supplier $"
                  className="h-8 text-sm text-right"
                  data-testid={`${testIdPrefix}-orp`}
                />
              </div>
            )}
            {showOrp && (
              <div className="col-span-5 md:col-span-2">
                <label className="text-[10px] uppercase tracking-wide text-slate-500">Supplier</label>
                <SupplierCombobox
                  value={draft.parts_supplier}
                  onChange={(v) => setDraft(d => ({ ...d, parts_supplier: v }))}
                  testId={`${testIdPrefix}-supplier`}
                />
              </div>
            )}
            {!isTyre && (
              <div className="col-span-3 md:col-span-1">
                <label
                  className="text-[10px] uppercase tracking-wide text-slate-500"
                  title="RRP — what customer is charged. Drives line total."
                >
                  RRP $
                </label>
                <Input
                  type="number" min="0" step="0.01"
                  value={draft.parts_price}
                  onChange={(e) => setDraft(d => ({ ...d, parts_price: e.target.value }))}
                  placeholder="retail $"
                  className="h-8 text-sm text-right"
                  data-testid={`${testIdPrefix}-rrp`}
                />
              </div>
            )}
            <div className="col-span-3 md:col-span-1">
              <label className="text-[10px] uppercase tracking-wide text-slate-500">Margin %</label>
              <Input
                type="number" min="0" step="1"
                value={draft.parts_margin_pct}
                onChange={(e) => setDraft(d => ({ ...d, parts_margin_pct: e.target.value }))}
                placeholder={isTyre ? 'manual %' : undefined}
                title={isTyre ? 'Tyre margin is manual — you type it. Blank = 0%.' : undefined}
                className="h-8 text-sm text-right"
                data-testid={`${testIdPrefix}-margin`}
              />
            </div>
          </>
        )}

        {/* Tyre flat-rate only on tyre rows */}
        {isTyre && (
          <div className="col-span-4 md:col-span-2">
            <label
              className="text-[10px] uppercase tracking-wide text-amber-700"
              title="Per-tyre flat fee (fitting + balancing + valve + disposal) — added on top of ORP × (1 + margin%)."
            >
              Flat $ / tyre
            </label>
            <Input
              type="number" min="0" step="0.5"
              value={draft.tyre_flat_rate}
              onChange={(e) => setDraft(d => ({ ...d, tyre_flat_rate: e.target.value }))}
              placeholder="fit+bal+valve"
              className="h-8 text-sm text-right border-amber-300 focus-visible:ring-amber-200"
              data-testid={`${testIdPrefix}-tyre-flat`}
            />
          </div>
        )}

        {/* Labour block — hidden on part-only.
            For tyre rows we still show the Hours input (re-labelled
            "Alloc Hrs") so SM/FM can credit mechanics for fitting time —
            the value flows into the JC task's quoted_labour_hours and
            does NOT affect the line total (pricing stays ORP×1.30 + flat). */}
        {!hideLabour && (
          <>
            <div className="col-span-3 md:col-span-1">
              <label
                className="text-[10px] uppercase tracking-wide text-slate-500"
                title={isTyre
                  ? 'Allocated hours for fitting these tyres — credits the mechanic for productivity. Does NOT change the price (price is ORP × (1 + margin%) + flat).'
                  : 'Set to 0 for goodwill labour.'}
              >
                {isTyre ? 'Alloc Hrs' : 'Hrs'}
              </label>
              <Input
                type="number" min="0" step="0.25"
                value={draft.labour_hours}
                onChange={(e) => setDraft(d => ({ ...d, labour_hours: e.target.value }))}
                className="h-8 text-sm text-right"
                data-testid={`${testIdPrefix}-hours`}
              />
            </div>
            {!isTyre && (
              <div className="col-span-3 md:col-span-1">
                <label className="text-[10px] uppercase tracking-wide text-slate-500">Rate $/h</label>
                <Input
                  type="number" min="0" step="1"
                  value={draft.labour_rate}
                  onChange={(e) => setDraft(d => ({ ...d, labour_rate: e.target.value }))}
                  className="h-8 text-sm text-right"
                  data-testid={`${testIdPrefix}-rate`}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer — totals + cost margin chip + action buttons */}
      <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-[11px] text-slate-600">
            {isTyre
              ? `Per tyre ${fmt((totals.parts_charge || 0) + (Number(draft.tyre_flat_rate) || 0))} × ${Number(draft.quantity) || 1} qty · margin ${Number(draft.parts_margin_pct) || 0}%`
              : `Parts ${fmt(totals.parts_charge)} · Labour ${fmt(totals.labour_charge)}`}
          </p>
          {totals.cost_margin_pct != null && (
            <span
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${
                totals.cost_margin_pct < 0 ? 'border-rose-300 bg-rose-50 text-rose-800' :
                totals.cost_margin_pct < 30 ? 'border-amber-300 bg-amber-50 text-amber-800' :
                'border-emerald-300 bg-emerald-50 text-emerald-800'
              }`}
              title="Effective margin from cost: (RRP − ORP) / ORP × 100"
              data-testid={`${testIdPrefix}-cost-margin`}
            >
              {totals.cost_margin_pct >= 0 ? '+' : ''}{totals.cost_margin_pct.toFixed(1)}% from cost
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <span className="text-[10px] uppercase tracking-wide text-slate-500 mr-1">Total</span>
            <span className="font-bold text-slate-900 text-sm tabular-nums" data-testid={`${testIdPrefix}-total`}>
              {fmt(totals.line_total)}
            </span>
          </div>
          <Button size="sm" variant="ghost" onClick={onCancel} className="h-7 text-xs text-slate-500" data-testid={`${testIdPrefix}-cancel`}>
            <X className="h-3 w-3 mr-1" /> Cancel
          </Button>
          <Button
            size="sm"
            onClick={onCommit}
            disabled={!(draft.description || '').trim()}
            className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
            data-testid={`${testIdPrefix}-commit`}
          >
            {isEdit ? <><Check className="h-3 w-3 mr-1" /> Save</> : <><Plus className="h-3 w-3 mr-1" /> Add Row</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function QuoteBuilder({
  rows: rowsProp,
  onChange,
  showOrp = true,
}) {
  // iter141 — Fully controlled component. Parent owns `rows`; we treat
  // `rowsProp` as the single source of truth and forward mutations
  // through onChange. This eliminates the bidirectional sync loop that
  // caused "Maximum update depth exceeded" (iter148 crash) when the SM
  // committed a 2nd row — the previous design tracked rows in BOTH
  // internal state AND the parent, and the round-trip useEffect chain
  // hit React's nested-update ceiling under StrictMode + concurrent
  // batching.
  //
  // What stays internal:
  //   • adding (boolean — editor open/closed)
  //   • draft / editingId / editDraft (transient editor state)
  // What's now controlled:
  //   • rows — read from rowsProp on every render; mutations go via
  //     emitChange() which calls the parent's onChange immediately.
  const rows = Array.isArray(rowsProp) ? rowsProp : [];
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(EMPTY_DRAFT);
  // iter225 — per-site pricing defaults seed NEW drafts only.
  const [pricingDefaults, setPricingDefaults] = useState(null);
  useEffect(() => {
    let live = true;
    getPricingDefaults().then(d => {
      if (!live) return;
      setPricingDefaults(d);
      setDraft(prev => (prev === EMPTY_DRAFT ? blankDraft(d) : prev));
    });
    return () => { live = false; };
  }, []);

  // Stable ref to onChange so we can call it from any handler without
  // listing it as a dep (which would re-fire effects when the parent
  // recreates the callback).
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  const emitChange = (next) => {
    // Only emit committed rows (description non-empty). Buffered draft
    // state never reaches the parent. Matches pre-iter141 contract.
    onChangeRef.current?.(next.filter(r => (r.description || '').trim()));
  };

  // Live totals for editors
  const draftTotals = useMemo(() => computeRowPricing(draft), [draft]);
  const editTotals = useMemo(() => computeRowPricing(editDraft), [editDraft]);

  const resetDraft = () => setDraft(blankDraft(pricingDefaults));

  const commitDraft = () => {
    if (!(draft.description || '').trim()) {
      toast.error('Description is required');
      return;
    }
    const row = buildRowFromDraft(draft, draftTotals);
    row.order = rows.length;
    emitChange([...rows, row]);
    resetDraft();
    setAdding(false);
  };

  const startEdit = (row) => {
    setEditingId(row.line_id);
    setEditDraft(rowToDraft(row, pricingDefaults));
  };
  const commitEdit = () => {
    if (!(editDraft.description || '').trim()) {
      toast.error('Description is required');
      return;
    }
    const updated = buildRowFromDraft(editDraft, editTotals);
    emitChange(
      rows.map(r => r.line_id === editingId
        ? { ...updated, line_id: r.line_id, order: r.order }
        : r)
    );
    setEditingId(null);
  };
  const cancelEdit = () => setEditingId(null);

  const removeRow = (line_id) => {
    emitChange(rows.filter(r => r.line_id !== line_id));
  };

  const totals = useMemo(() => {
    // iter139.1 — Prices entered into Quote Builder are GST-INCLUSIVE
    // by user policy. We expose just `total` so the Quote Builder
    // footer + SMS template don't show a misleading "+10% GST" line.
    const total = rows.reduce((s, r) => s + (Number(r.line_total) || 0), 0);
    return { total };
  }, [rows]);

  return (
    <div className="border border-slate-200 rounded-lg bg-white" data-testid="quote-builder">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b bg-slate-50 rounded-t-lg">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs uppercase tracking-wide font-semibold text-slate-700">Quote Builder</span>
          {rows.length > 0 && (
            <span className="text-[10px] text-slate-500">
              {rows.length} line{rows.length === 1 ? '' : 's'} · total {fmt(totals.total)}
            </span>
          )}
        </div>
        {!adding && !editingId && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => { setDraft(blankDraft(pricingDefaults)); setAdding(true); }}
            data-testid="qb-add-row-btn"
            className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          >
            <Plus className="h-3 w-3 mr-1" /> Add Row
          </Button>
        )}
      </div>

      {/* Add Row Editor */}
      {adding && (
        <div className="p-3 border-b border-emerald-100 bg-emerald-50/20">
          <EditorBlock
            draft={draft}
            setDraft={setDraft}
            totals={draftTotals}
            onCommit={commitDraft}
            onCancel={() => { setAdding(false); resetDraft(); }}
            showOrp={showOrp}
            isEdit={false}
            testIdPrefix="qb-editor"
          />
        </div>
      )}

      {/* Rows list */}
      {rows.length === 0 && !adding ? (
        <div className="px-4 py-6 text-center text-sm text-slate-400" data-testid="qb-empty">
          No quote rows yet. Click <span className="font-semibold text-slate-600">Add Row</span> to start building.
        </div>
      ) : (
        <ul className="divide-y divide-slate-200">
          {rows.map(r => {
            const isEditing = editingId === r.line_id;
            if (isEditing) {
              return (
                <li key={r.line_id} className="p-3 bg-blue-50/30" data-testid={`qb-row-edit-${r.line_id}`}>
                  <EditorBlock
                    draft={editDraft}
                    setDraft={setEditDraft}
                    totals={editTotals}
                    onCommit={commitEdit}
                    onCancel={cancelEdit}
                    showOrp={showOrp}
                    isEdit={true}
                    testIdPrefix={`qb-edit-${r.line_id}`}
                  />
                </li>
              );
            }
            return (
              <li key={r.line_id} className="px-4 py-2.5 flex items-center gap-3 hover:bg-slate-50" data-testid={`qb-row-${r.line_id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{r.description}</p>
                  <p className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded text-[10px] font-medium">
                      <Wrench className="h-2.5 w-2.5" />
                      {(CATEGORY_OPTIONS.find(o => o.value === (r.category || 'part_and_labour')) || {}).label || 'Part & Labour'}
                    </span>
                    {r.parts_supplier && <span>@ {r.parts_supplier}</span>}
                    {(Number(r.parts_price) || 0) > 0 && <span>RRP {fmt(r.parts_price)}</span>}
                    {(Number(r.parts_margin_pct) || 0) > 0 && <span>{r.parts_margin_pct}% margin</span>}
                    {(Number(r.labour_hours) || 0) > 0 && (
                      (r.category === 'tyre')
                        ? <span>{r.labour_hours}h allocated</span>
                        : <span>{r.labour_hours}h × {fmt(r.labour_rate)}</span>
                    )}
                    {(Number(r.tyre_flat_rate) || 0) > 0 && <span>flat {fmt(r.tyre_flat_rate)}/tyre</span>}
                  </p>
                </div>
                <p className="text-xs text-slate-500 tabular-nums w-12 text-right">× {r.quantity ?? 1}</p>
                <p className="text-sm font-bold text-slate-900 tabular-nums w-24 text-right">{fmt(r.line_total)}</p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => startEdit(r)}
                    className="text-slate-500 hover:text-slate-900 p-1.5 rounded hover:bg-slate-200"
                    title="Edit row"
                    aria-label="Edit row"
                    data-testid={`qb-edit-${r.line_id}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => removeRow(r.line_id)}
                    className="text-slate-400 hover:text-rose-600 p-1.5 rounded hover:bg-rose-50"
                    title="Remove row"
                    aria-label="Remove row"
                    data-testid={`qb-remove-${r.line_id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Footer — totals (iter139.1: GST-inclusive prices, no GST line) */}
      <div className="px-4 py-2.5 border-t border-slate-200 bg-slate-50 rounded-b-lg flex items-center justify-end gap-4 text-xs flex-wrap">
        <span className="text-blue-900">Total: <span className="font-bold tabular-nums" data-testid="qb-total">{fmt(totals.total)}</span></span>
      </div>
    </div>
  );
}

/**
 * Build a customer-facing SMS body from a list of quote rows + workshop name.
 * Kept here so QuickBookingModal and QuoteDetail use the SAME template.
 *
 * iter139.1 — User policy: prices entered into Quote Builder are
 * GST-INCLUSIVE. The SMS template MUST NOT mention GST at all (per the
 * user — it caused customer confusion). Just show the line totals and
 * one grand-total line. No subtotal / GST / "incl GST" footer.
 */
export { EditorBlock, blankDraft, buildRowFromDraft, rowToDraft, CATEGORY_OPTIONS };

export function buildQuoteSmsBody({ rows, customerName, vehicleRego, workshopName = 'Hoppers Service Centre', quoteNumber }) {
  const cleanedRows = (rows || []).filter(r => (r.description || '').trim());
  const lines = cleanedRows.map((r, i) =>
    `${i + 1}. ${r.description} — $${Number(r.line_total || 0).toFixed(2)}`
  );
  const total = cleanedRows.reduce((s, r) => s + (Number(r.line_total) || 0), 0);
  return [
    `Hi ${customerName || 'there'},`,
    `Here's the quote you asked about${vehicleRego ? ` for ${vehicleRego}` : ''}${quoteNumber ? ` (${quoteNumber})` : ''}:`,
    '',
    ...lines,
    '',
    `Total: $${total.toFixed(2)}`,
    '',
    `Reply YES to book or call us if you'd like to discuss.`,
    `— ${workshopName}`,
  ].join('\n');
}
